import { useRef } from 'react';
import styled from 'styled-components';

import { Slide } from 'common/api-types/director';
import { media } from 'common/media';

import { ImageLargeSlide } from './ImageLargeSlide';
import { ImageSmallSlide } from './ImageSmallSlide';
import { ChangeSlideButton } from '../components/ChangeSlideButton';

const NextClickArea = styled.div`
  position: absolute;
  top: 79px;
  bottom: 0;
  right: 0;
  left: 30%;
  user-select: none;
`;

const PreviousClickArea = styled.div`
  position: absolute;
  top: 79px;
  bottom: 0;
  left: 0;
  right: 70%;
`;

// button height is 38px, footer height minus header height divided by 2 is 45px
const PreviousButton = styled(ChangeSlideButton)`
  position: absolute;
  top: calc(50% - (36px / 2) - 45px);
  left: 8px;
  @media ${media.lg} {
    left: 24px;
  }
`;

// button height is 38px, footer height minus header height divided by 2 is 45px
const NextButton = styled(ChangeSlideButton)`
  position: absolute;
  top: calc(50% - (36px / 2) - 45px);
  right: 8px;
  @media ${media.lg} {
    right: 24px;
  }
  user-select: none;
`;

export const Slides: React.FC<{
  slides: Slide[];
  currentStep: number;
  totalSteps: number;
  onPauseAction: () => void;
  onNextStep: () => void;
  onPreviousStep: () => void;
  isPaused: boolean;
}> = ({ slides, currentStep, totalSteps, onPauseAction, onNextStep, onPreviousStep, isPaused }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);

  const isOnFirstStep = currentStep === 0;
  const isOnLastStep = currentStep === totalSteps - 1;
  const currentSlide = slides[currentStep];
  const description = currentSlide?.description?.value || '';

  const handleTouchStart = () => {
    timer.current = setTimeout(() => {
      onPauseAction();
    }, 200);
  };

  const handlePreviousTouchEnd = () => {
    clearTimeout(timer.current as NodeJS.Timeout);
    if (isPaused) {
      onPauseAction();
    } else {
      onPreviousStep();
    }
  };

  const handleNextTouchEnd = () => {
    clearTimeout(timer.current as NodeJS.Timeout);

    if (isPaused) {
      onPauseAction();
    } else {
      onNextStep();
    }
  };

  return (
    <>
      {currentSlide.variant === 'IMAGE_LARGE' && (
        <ImageLargeSlide
          description={description}
          imageUrl={currentSlide.image.url}
          isOnFirstStep={isOnFirstStep}
          isOnLastStep={isOnLastStep}
          handleTouchStart={handleTouchStart}
          handleNextTouchEnd={handleNextTouchEnd}
          onNextStep={onNextStep}
          handlePreviousTouchEnd={handlePreviousTouchEnd}
          onPreviousStep={onPreviousStep}
        />
      )}
      {currentSlide.variant === 'IMAGE_SMALL' && (
        <ImageSmallSlide
          description={description}
          imageUrl={currentSlide.image.url}
          isOnFirstStep={isOnFirstStep}
          isOnLastStep={isOnLastStep}
          handleTouchStart={handleTouchStart}
          handleNextTouchEnd={handleNextTouchEnd}
          onNextStep={onNextStep}
          handlePreviousTouchEnd={handlePreviousTouchEnd}
          onPreviousStep={onPreviousStep}
        />
      )}
      {!isOnFirstStep && (
        <>
          <PreviousClickArea
            role="button"
            onTouchStart={handleTouchStart}
            onMouseUp={handlePreviousTouchEnd}
          />
          <PreviousButton variant="previous" onClick={onPreviousStep} />
        </>
      )}
      {!isOnLastStep && (
        <>
          <NextClickArea
            role="button"
            onTouchStart={handleTouchStart}
            onMouseUp={handleNextTouchEnd}
          />
          <NextButton variant="next" onClick={onNextStep} />
        </>
      )}
    </>
  );
};
